import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { cn } from "@utils";
import { useMemo, useState } from "react";

export interface SearchableSelectItem {
  id: string;
  label: string;
  /** Optional extra text included in the search match (e.g. level code). */
  searchText?: string;
}

interface SearchableSelectProps {
  items: SearchableSelectItem[];
  value: string;
  onChange: (id: string) => void;
  /** Text shown in the input when nothing is selected and nothing is typed. */
  emptyLabel?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Accessible combobox that lets the user type to filter a long list of options.
 * Built on Headless UI's Combobox. Keeps a plain (id, label) contract so it can
 * back any selector (e.g. the attendance class picker).
 */
function SearchableSelect({
  items,
  value,
  onChange,
  emptyLabel = "Choose...",
  className,
  disabled = false,
}: SearchableSelectProps) {
  const [query, setQuery] = useState("");

  const selected = items.find((item) => item.id === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) =>
      `${item.label} ${item.searchText ?? ""}`.toLowerCase().includes(q),
    );
  }, [items, query]);

  return (
    <Combobox
      value={selected}
      onChange={(item: SearchableSelectItem | null) =>
        onChange(item?.id ?? "")
      }
      disabled={disabled}
    >
      <div className={cn("relative", className)}>
        <div className="relative">
          <ComboboxInput
            className="w-full px-4 py-3 pr-10 bg-earth-50 border border-earth-200 rounded-lg text-earth-800 font-medium focus:outline-none focus:ring-2 focus:ring-earth-500 focus:border-transparent disabled:opacity-50"
            placeholder={selected ? undefined : emptyLabel}
            displayValue={(item: SearchableSelectItem | null) =>
              item?.label ?? ""
            }
            onChange={(e) => setQuery(e.target.value)}
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-3 text-earth-400">
            <i className="ri-arrow-down-s-line text-lg" aria-hidden="true"></i>
          </ComboboxButton>
        </div>

        <ComboboxOptions className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white border border-earth-200 py-1 shadow-lg focus:outline-none">
          {filtered.length === 0 ? (
            <div className="px-4 py-2 text-sm text-earth-700">
              No matches for "{query}"
            </div>
          ) : (
            filtered.map((item) => (
              <ComboboxOption
                key={item.id}
                value={item}
                className="cursor-pointer select-none px-4 py-2 text-sm text-earth-700 data-[focus]:bg-earth-100 data-[selected]:font-semibold"
              >
                {item.label}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}

export default SearchableSelect;
